import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Container } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useScheduledMessagesWebSocket } from 'src/context/ScheduledMessagesWebSocketContext';
import Page from 'src/components/Page';
import Spinner from 'src/components/Spinner/Spinner';
import Header from 'src/components/View/Header';
import Results from 'src/views/management/ScheduledMessagesView/Results';
import SearchBox from 'src/views/management/ScheduledMessagesView/SearchBox';
import ButtonBox from 'src/views/management/ScheduledMessagesView/ButtonBox';
import {
  setScheduledMessagesQuery,
  getScheduledMessages,
  setScheduledMessages,
  clearBackToScheduledMessagesList,
  setBackToScheduledMessagesList,
  socketScheduledMessageSentInList,
} from 'src/actions/scheduledMessagesActions';
import {
  moveScrollDashboardLayoutContentToPosition,
  moveScrollPerfectScrollbarTableToPosition,
} from 'src/actions/scrollActions';
import helpers from 'src/helpers';
import {
  MESSAGES,
  PERMISSIONS,
  ROUTES,
  SOCKET,
} from 'src/constants';

import pageViewStyle from 'src/components/View/Page/pageView.style';

const useStyles = makeStyles()(pageViewStyle);

const EDIT_MESSAGE_ROUTES = {
  [MESSAGES.SCHEDULED_MESSAGE_TYPES.ORDER_FOLLOW_UP_MESSAGE]: (id) => `${ROUTES.APP_MANAGEMENT_SCHEDULED_MESSAGES}/follow-up-edit/${id}`,
  [MESSAGES.SCHEDULED_MESSAGE_TYPES.EVENT_SMART_ACTION_MESSAGE]: (id) => `${ROUTES.APP_MANAGEMENT_SCHEDULED_MESSAGES}/smart-action-edit/${id}`,
  [MESSAGES.SCHEDULED_MESSAGE_TYPES.SCHEDULED_MESSAGE]: (id) => `${ROUTES.APP_MANAGEMENT_SCHEDULED_MESSAGES}/manually-scheduled-edit/${id}`,
  [MESSAGES.SCHEDULED_MESSAGE_TYPES.SCHEDULED_BIRTHDAY_MESSAGE]: (id) => `${ROUTES.APP_MANAGEMENT_SCHEDULED_MESSAGES}/birthday-message-edit/${id}`,
  [MESSAGES.SCHEDULED_MESSAGE_TYPES.GROUP_SMART_ACTION_MESSAGE]: (id) => `${ROUTES.APP_MANAGEMENT_SCHEDULED_MESSAGES}/group-smart-action-edit/${id}`,
  // Host Invite records live in the scheduledMessages table (same shape as
  // manually-scheduled messages). Route to the manually-scheduled edit view
  // for now — the view works generically with any scheduledMessages row.
  [MESSAGES.SCHEDULED_MESSAGE_TYPES.HOST_INVITE_MESSAGE]: (id) => `${ROUTES.APP_MANAGEMENT_SCHEDULED_MESSAGES}/manually-scheduled-edit/${id}`,
};

const { SOCKET_EVENTS } = SOCKET;

function ScheduledMessagesViewComponent() {
  const { classes } = useStyles();
  const scheduledMessagesSocketService = useScheduledMessagesWebSocket();
  const dispatch = useDispatch();
  const history = useHistory();
  const [shouldScrollChange, setShouldScrollChange] = useState(null);

  const {
    scheduledMessages,
    selectedScheduledMessages,
    query,
    loading,
    visibleModalFormNumber,
    backToScheduledMessagesList,
  } = useSelector((state) => state?.scheduledMessages || {});

  const {
    userRole,
  } = useSelector((state) => state.account?.user || {});

  const {
    permissions: userRolePermissions,
  } = userRole || {};
  const {
    comeBack,
    scheduledMessages: backToScheduledMessagesListMessages,
    query: backToScheduledMessagesQuery,
    backToId,
    initScrollToId,
  } = backToScheduledMessagesList || {};

  const canEditMessages = helpers.permissions.doesUserHavePermission(PERMISSIONS.PERMISSIONS_SYSTEM_CODE.SCHEDULED_MESSAGES_EDIT, userRolePermissions);
  const canFilterByBirthdayMessage = helpers.permissions.doesUserHavePermission(PERMISSIONS.PERMISSIONS_SYSTEM_CODE.SCHEDULED_CONNECTION_FILTER_BY_BIRTHDAY_MESSAGE, userRolePermissions);
  const canFilterByHostInvite = helpers.permissions.doesUserHavePermission(PERMISSIONS.PERMISSIONS_SYSTEM_CODE.EVENT_INVITES_TAB_VIEW, userRolePermissions);
  const goToUrl = useCallback((url) => history.push(url), [history]);

  const onChangeSortBy = (sortValue) => {
    dispatch(setScheduledMessagesQuery({
      sort: sortValue,
      page: 1,
    }));
    dispatch(getScheduledMessages());
  };

  const onChangeStatus = (status) => {
    const defaultSort = status === 'sent' ? 'desc' : 'asc';
    dispatch(setScheduledMessagesQuery({
      status,
      sort: [['dateTime', defaultSort]],
      page: 1,
    }));
    dispatch(getScheduledMessages());
  };

  const setSearch = (search) => {
    dispatch(setScheduledMessagesQuery({
      search,
      page: 1,
    }));
  };

  const onSearch = () => {
    dispatch(getScheduledMessages());
  };

  const getNextScrollById = useCallback(() => {
    const goToId = document.getElementById(`scheduledMessageId${shouldScrollChange}`);
    const scrollBox = document.getElementById('scrollBox');
    if (goToId && scrollBox) {
      const {
        height: scrollBoxHeight,
      } = scrollBox?.getBoundingClientRect() || {};
      const offsetTopEl = helpers.transform.toInt10(goToId?.offsetTop || 0);
      const offsetTopParentEl = helpers.transform.toInt10(goToId?.parentElement?.offsetTop || 0);
      const offsetHeightParentEl = helpers.transform.toInt10(goToId?.parentElement?.offsetHeight || 0);
      const perfectScrollbarTableY = offsetTopEl - offsetTopParentEl;
      const dashboardLayoutContentY = (perfectScrollbarTableY + helpers.transform.toInt10(scrollBoxHeight || 0) - offsetTopParentEl) - offsetHeightParentEl;

      return {
        dashboardLayoutContentY: dashboardLayoutContentY > 0 ? dashboardLayoutContentY : 0,
        perfectScrollbarTableY: perfectScrollbarTableY > 0 ? perfectScrollbarTableY : 0,
      };
    }
    return {};
  }, [shouldScrollChange]);

  const setCopyValuesToBack = (clickedMessageId = null, clickedMessageType = null) => {
    dispatch(setBackToScheduledMessagesList({
      scheduledMessages,
      query,
      active: true,
      comeBack: false,
      backToId: clickedMessageId ? `${clickedMessageId}-${clickedMessageType}` : null,
      initScrollToId: false,
    }));
  };

  const handleRowClick = (event, id, messageType) => {
    if (!canEditMessages || window.getSelection().toString()) {
      return;
    }

    const getRoute = EDIT_MESSAGE_ROUTES[messageType];
    if (getRoute) {
      const path = getRoute(id);
      setCopyValuesToBack(id, messageType);
      setTimeout(() => {
        goToUrl(path);
      }, 100);
    }
  };

  const handleScheduledMessageSent = (message) => {
    dispatch(socketScheduledMessageSentInList(message));
  };

  useEffect(() => {
    if (comeBack && !helpers.isEmpty(backToScheduledMessagesListMessages) && !helpers.isEmpty(backToScheduledMessagesQuery)) {
      dispatch(setScheduledMessagesQuery(backToScheduledMessagesQuery));
      dispatch(getScheduledMessages());
    } else {
      dispatch(setScheduledMessagesQuery());
      dispatch(getScheduledMessages());
    }

    // Scrolling logic
    if ((comeBack || initScrollToId) && backToId) {
      setShouldScrollChange(backToId);
    }

    dispatch(clearBackToScheduledMessagesList(true));

    return () => {
      dispatch(setScheduledMessages(false));
      dispatch(setScheduledMessagesQuery(false));
    };
  }, [dispatch]);

  useEffect(() => {
    if (scheduledMessagesSocketService) {
      scheduledMessagesSocketService.on(SOCKET_EVENTS.SCHEDULED_MESSAGE_MANUALLY_SENT, handleScheduledMessageSent);
      scheduledMessagesSocketService.on(SOCKET_EVENTS.SCHEDULED_MESSAGE_BIRTHDAY_SENT, handleScheduledMessageSent);
      scheduledMessagesSocketService.on(SOCKET_EVENTS.SCHEDULED_MESSAGE_SMART_ACTION_SENT, handleScheduledMessageSent);
      scheduledMessagesSocketService.on(SOCKET_EVENTS.SCHEDULED_MESSAGE_ORDER_FOLLOW_UP_SENT, handleScheduledMessageSent);
      scheduledMessagesSocketService.on(SOCKET_EVENTS.SCHEDULED_MESSAGE_CAMPAIGN_SENT, handleScheduledMessageSent);
      scheduledMessagesSocketService.on(SOCKET_EVENTS.SCHEDULED_MESSAGE_GROUP_SMART_ACTION_SENT, handleScheduledMessageSent);
    }

    return () => {
      if (scheduledMessagesSocketService) {
        scheduledMessagesSocketService.off(SOCKET_EVENTS.SCHEDULED_MESSAGE_MANUALLY_SENT, handleScheduledMessageSent);
        scheduledMessagesSocketService.off(SOCKET_EVENTS.SCHEDULED_MESSAGE_BIRTHDAY_SENT, handleScheduledMessageSent);
        scheduledMessagesSocketService.off(SOCKET_EVENTS.SCHEDULED_MESSAGE_SMART_ACTION_SENT, handleScheduledMessageSent);
        scheduledMessagesSocketService.off(SOCKET_EVENTS.SCHEDULED_MESSAGE_ORDER_FOLLOW_UP_SENT, handleScheduledMessageSent);
        scheduledMessagesSocketService.off(SOCKET_EVENTS.SCHEDULED_MESSAGE_CAMPAIGN_SENT, handleScheduledMessageSent);
        scheduledMessagesSocketService.off(SOCKET_EVENTS.SCHEDULED_MESSAGE_GROUP_SMART_ACTION_SENT, handleScheduledMessageSent);
      }
    };
  }, [scheduledMessagesSocketService]);

  useEffect(() => {
    if (shouldScrollChange && !loading) {
      setShouldScrollChange(null);
      const shouldScrollChangeId = shouldScrollChange === true ? 0 : shouldScrollChange;
      if (shouldScrollChangeId) {
        const { dashboardLayoutContentY, perfectScrollbarTableY } = getNextScrollById() || {};
        dispatch(moveScrollDashboardLayoutContentToPosition(0, dashboardLayoutContentY));
        dispatch(moveScrollPerfectScrollbarTableToPosition(0, perfectScrollbarTableY));
      }
    }
  }, [shouldScrollChange, loading, getNextScrollById, dispatch]);

  return (
    <Page className={classes.root} title="Scheduled Connections">
      <Container maxWidth={false}>
        <Header
          title="Scheduled Connections"
          searchBox={(
            <SearchBox
              loading={loading}
              query={query}
              onSearch={onSearch}
              setSearch={setSearch}
              setCopyValuesToBack={setCopyValuesToBack}
              onChangeStatus={onChangeStatus}
              canFilterByBirthdayMessage={canFilterByBirthdayMessage}
              canFilterByHostInvite={canFilterByHostInvite}
            />
          )}
          buttonBox={(
            <ButtonBox
              classes={classes}
            />
          )}
        />
        <Box
          className={classes.relativeBox}
          mt={3}
        >
          <Spinner
            loading={loading}
            positionType="absolute"
          />
          <Results
            loading={loading}
            query={query}
            scheduledMessages={scheduledMessages}
            selectedScheduledMessages={selectedScheduledMessages}
            userRolePermissions={userRolePermissions}
            visibleModalFormNumber={visibleModalFormNumber}
            onChangeSortBy={onChangeSortBy}
            handleRowClick={handleRowClick}
            setCopyValuesToBack={setCopyValuesToBack}
          />
        </Box>
      </Container>
    </Page>
  );
}

export default ScheduledMessagesViewComponent;
